import {StyledModal} from "Components/Common/StyledModal";
import {ArchiveIcon} from "Components/Icons/ArchiveIcon";
import {CalendarIcon} from "Components/Icons/CalendarIcon";
import {CopyIcon} from "Components/Icons/CopyIcon";
import {EditIcon} from "Components/Icons/EditIcon";
import {StrategicPlanItem, StrategicPlanParent, StrategicPlanStore} from "Stores/strategicPlanStore";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Accordion, OverlayTrigger, Tooltip} from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import {StatusType} from "./strategicPlans";
import ControlledInput from "Components/Common/ControlledInput";
import {useForm} from "react-hook-form";
import {EAIClient} from "Services/EAIClient";
import {CustomToggle} from "./CustomToggle";
import {ItemContainer} from "./ItemContainer";
import {ApiResponse, CreateItemResponse, CustomFormData, PlanFormData} from "./interfaces";
import getFormatedDate from "../../../utils/dateUtils";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";


interface EditStrategicPlanFormProps {
    strategicPlan?: StrategicPlanParent;
    onEdit: boolean;
    setOnEdit: () => void;
    setDndChanged: (e: boolean) => void;
    setDatesChanged: (e: boolean) => void;
    onArchivePlan: (plan: Partial<StrategicPlanParent>) => void;
    onArchiveItem: (plan: Partial<StrategicPlanItem>) => void;
    onDelete: (planItem: Partial<StrategicPlanItem>) => void;
    loadStrategicPlans: () => Promise<void>;
    onCopyPlan: (plan: Partial<StrategicPlanParent>) => void;
    showAlert?: (message: string) => void;
    showProcessing: (e: boolean) => void;
    disable?: boolean;
    onFormValidityChange?: (isValid: boolean) => void;
    handleCloseOnEdit: () => void;
    strategicPlanStore?: StrategicPlanStore;
    formData?: CustomFormData
}


type ModalActions = "archive" | "delete" | "unarchive" | "nonDelete" | "archiveItem" | "unArchiveItem" | "copyPlan";
type EditableData = { [key: number]: Array<StrategicPlanItem> };


const getModalTitle = (openModal: ModalActions | null) => {
    if (openModal === "archive") { return "Are you sure you want to archive this Plan?"; }
    else if (openModal === "delete") { return "Delete plan item?"; }
    else if (openModal === "unarchive") { return "Are you sure you want to unarchive this Plan?"; }
    else if (openModal === "nonDelete") { return "This Plan Item can't be deleted"; }
    else if (openModal === "archiveItem") { return "Are you sure you want to archive this Plan Item?"; }
    else if (openModal === "unArchiveItem") { return "Are you sure you want to unarchive this Plan Item?"; }
    else if (openModal === "copyPlan") { return "Duplicate Plan"; }
    else { return ""; }
};

const getConfirmLabel = (openModal: ModalActions | null) => {
    if (openModal === "archive") { return "Archive plan"; }
    else if (openModal === "delete") { return "Delete plan item"; }
    else if (openModal === "unarchive") { return "Unarchive plan"; }
    else if (openModal === "nonDelete") { return "Got it"; }
    else if (openModal === "archiveItem") { return "Archive plan item"; }
    else if (openModal === "unArchiveItem") { return "Unarchive plan item"; }
    else if (openModal === "copyPlan") { return "Duplicate"; }
    else { return ""; }
};

const getSubtitleModal = (openModal: ModalActions | null) => {
    if (openModal === "archive") { return ""; }
    else if (openModal === "delete") { return "Delete plan item will also delete all other items that might be associated to it. \n Do you want to continue?"; }
    else if (openModal === "unarchive") { return ""; }
    else if (openModal === "nonDelete") { return "Item Plans with ActionPlans attached can not be deleted. To delete a Plan Item, you must re-assign or delete the ActionPlans associated to it to a different one."; }
    else if (openModal === "archiveItem") { return ""; }
    else if (openModal === "unArchiveItem") { return ""; }
    else { return ""; }
};

const EditStrategicPlanFormContext = React.createContext<EditStrategicPlanFormProps | Partial<EditStrategicPlanFormProps>>({});


const canDelete = (strategic_plan: StrategicPlanItem) => {
    if ((strategic_plan?.action_plan_statuses?.open_time === 0 &&
        strategic_plan?.action_plan_statuses?.open_late === 0 &&
        strategic_plan?.action_plan_statuses?.closed_goal_met === 0 &&
        strategic_plan?.action_plan_statuses?.closed_goal_not_met === 0 &&
        strategic_plan?.action_plan_statuses?.no_date_assigned === 0 &&
        strategic_plan?.total_children_action_plan_statuses?.open_time === 0 &&
        strategic_plan?.total_children_action_plan_statuses?.open_late === 0 &&
        strategic_plan?.total_children_action_plan_statuses?.closed_goal_met === 0 &&
        strategic_plan?.total_children_action_plan_statuses?.closed_goal_not_met === 0 &&
        strategic_plan?.total_children_action_plan_statuses?.no_date_assigned === 0) ||
        (!strategic_plan?.action_plan_statuses || !strategic_plan?.total_children_action_plan_statuses)
    ) {
        return true;
    }
    else { return false; }
};

const StatusCircle = ({ type, value }: { type: StatusType, value: number }) => {
    return (
        <OverlayTrigger placement="top" overlay={
            <Tooltip id={`tooltip-${type}-${value}`} className="tooltip-st-status">
                {type.replace(/(^\w{1})|(_+\w{1})/g, letter => letter.toUpperCase()).replace(/_/g, " ")}
            </Tooltip>
        }>
            <div className={`${type} status-circle`}>
                {value}
            </div>
        </OverlayTrigger>
    );
};


export const EditableStrategicPlanForm = ({
    strategicPlan,
    onEdit,
    setOnEdit,
    setDndChanged,
    setDatesChanged,
    onArchivePlan,
    onArchiveItem,
    onDelete,
    onCopyPlan,
    showAlert,
    showProcessing,
    formData,
    disable,
    loadStrategicPlans,
    handleCloseOnEdit
}: EditStrategicPlanFormProps) => {
    const [editableData, setEditableData] = useState<EditableData>({});
    const [droppableData, setDroppableData] = useState<StrategicPlanItem[]>([]);
    const [openModal, setOpenModal] = useState<ModalActions | null>(null);
    const [currentItem, setCurrentItem] = useState<StrategicPlanItem | null>(null);
    const [currentPlan, setCurrentPlan] = useState<StrategicPlanParent>();
    const [inputValue, setInputValue] = useState<string>("");
    const [changedItems, setChangedItems] = useState<StrategicPlanItem[]>([]);
    const [strategicPlanEdit, setStrategicPlanEdit] = useState<Partial<StrategicPlanParent>>();
    const toggleButtonRef = useRef<Record<string, HTMLButtonElement | null>>({});
    const [showDuplicatedValue, setShowDuplicatedValue] = useState(false);
    const [hasFormError, setHasFormError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("An error has occurred. Please try again.");
    const [datePeriod, setDatePeriod] = useState<{ start: Date | null, end: Date | null }>(
        {
            start: (strategicPlan?.start_date) ? new Date(strategicPlan?.start_date.replaceAll("-", "/")) : null,
            end: (strategicPlan?.end_date) ? new Date(strategicPlan?.end_date.replaceAll("-", "/")) : null
        });
    const api = new EAIClient();

    const findItemById = (id: number, items: StrategicPlanItem[], depth = 0) => {
        if (depth > 4) {
            return undefined;
        }
        for (const item of items) {
            if (item.id === id) {
                item.level = depth;
                return item;
            }
            if (item.children && item.children.length > 0) {
                const foundItem = findItemById(id, item.children, depth + 1);
                if (foundItem) {
                    foundItem.level = depth;
                    return foundItem;
                }
            }
        }
        return undefined;
    };

    const findParentItemByChildId = (childId: number, items: StrategicPlanItem[]) => {
        const foundItem = findItemById(childId, items);
        if (foundItem && foundItem.parent_id !== null) {
            return findItemById(foundItem.parent_id, items);
        }
        return undefined;
    };

    const getDisplayOrder = (siblings: StrategicPlanItem[], parentItem?: StrategicPlanItem) => {
        // const parentItem = parentItemId === 0 ? null : findItemById(parentItemId, renderableData.items);
        const level = parentItem && typeof parentItem.level === "number" ? parentItem.level + 1 : 0;
        const parentDisplayOrder = parentItem && parentItem.display_order ? parentItem.display_order : 0;
        // const siblings = parentItem ? parentItem.children : [];
        let lastOrder = 0;
        if (siblings.length > 0) {
            lastOrder = siblings[siblings.length-1].display_order || 0;
        }
        let displayOrder = 0;
        if (level === 0) {
            displayOrder = 1000000000 + lastOrder;
        } else if (level === 1) {
            displayOrder = 1000000 + lastOrder;
        } else if (level === 2) {
            displayOrder = 1000 + lastOrder;
        } else {
            displayOrder = lastOrder + 1;
        }
        return displayOrder + parentDisplayOrder;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        let alertMessage = "";
        if (openModal === "archiveItem") {
            onArchiveItem({ id: currentItem?.id ?? undefined, status: "archived", strategic_plan_ruuid: currentPlan?.ruuid });
            alertMessage = "Plan Item archived successfully.";
        } else if (openModal === "unArchiveItem") {
            onArchiveItem({ id: currentItem?.id, status: "active", strategic_plan_ruuid: currentPlan?.ruuid });
            alertMessage = "Plan Item unarchived successfully.";
        } else if (openModal === "archive") {
            onArchivePlan({ status: "archived", ruuid: currentPlan?.ruuid });
            alertMessage = "Plan archived successfully.";
        } else if (openModal === "unarchive") {
            onArchivePlan({ status: "active", ruuid: currentPlan?.ruuid });
            alertMessage = "Plan unarchived successfully.";
        } else if (openModal === "delete") {
            if (currentItem?.parent_id) {
                const parent = findItemById(currentItem?.parent_id ?? 0, renderableData.items);
                if (parent && parent.children.length === 1) {
                    // toggle parent accordion if it has 1 child
                    const button = document.getElementById(`toggle_${parent.id}`);
                    if (button) {
                        button.click();
                    }
                }
            } else {
                if (renderableData.items.length === 1) {
                    // toggle parent accordion if it has 1 child
                    const button = document.getElementById(`toggle_${renderableData.ruuid}`);
                    if (button) {
                        button.click();
                    }
                }
            }
            e.stopPropagation();
            onDelete({ id: currentItem?.id ?? 0 });
            alertMessage = "Item deleted successfully.";
        } else if (openModal === "nonDelete") {
            e.stopPropagation();
        }
        showAlert && showAlert(alertMessage);
        setOpenModal(null);

        setTimeout(() => {
            showAlert && showAlert("");
        }, 2500);
    };

    const generateUniqueId = () => {
        const now = new Date().getTime();
        // Use modulus to ensure the value doesn't exceed PostgreSQL's INTEGER max value
        const maxPostgresInt = 2147483647;
        return now % maxPostgresInt;
    };

    const addSubItem = useCallback((parent_id: number | string, strategic_plan_ruuid: string) => {
        setEditableData(currentEditableData => {
            const id = generateUniqueId();
            const parentItem = findItemById(parent_id as number, renderableData.items);
            const newItem: StrategicPlanItem = {
                id: id,
                school_id: "",
                name: "",
                status: "",
                parent_id: parent_id as number,
                strategic_plan_ruuid: strategic_plan_ruuid,
                created: "",
                updated: "",
                children: [],
                display_order: getDisplayOrder(parentItem?.children, parentItem)
            };

            const addChildToParent = (data: StrategicPlanItem) => {
                if (data.children) {
                    for (let i = 0; i < data.children.length; i++) {
                        const child = data.children[i];
                        if (child.id === parent_id) {
                            child.children.push(newItem);
                            return true;
                        } else if (addChildToParent(child)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            let itemAdded = false;
            Object.values(currentEditableData).flat().some((item) => {
                if (item.id! === parent_id) {
                    item?.children.push(newItem);
                    itemAdded = true;
                    return true;
                } else if (addChildToParent(item)) {
                    itemAdded = true;
                    return true;
                }
                return false;
            });

            if (!itemAdded) {
                currentEditableData[id.toString()] = newItem;
            }

            return { ...currentEditableData };
        });

        setTimeout(() => {
            const button = toggleButtonRef.current[parent_id];
            // verify if the item is already open
            if (button && button.querySelector("[data-testid=\"caretRight-icon\"]")) {
                button.click();
            }
        }, 10);

    }, [editableData]);

    const addData = useCallback((parent_id: number | string, strategic_plan_ruuid: string) => {
        setEditableData(currentEditableData => {
            const newEditableData = { ...currentEditableData };
            const id = generateUniqueId();
            newEditableData[id.toString()] = {
                id: id,
                school_id: "",
                name: "",
                status: "",
                parent_id: parent_id,
                strategic_plan_ruuid: strategic_plan_ruuid,
                display_order: getDisplayOrder(renderableData.items),
                created: "",
                updated: "",
                newItem: true,
                children: []
            } as StrategicPlanItem;
            return newEditableData;
        });
    }, [editableData]);

    const removeData = useCallback((parent_id: number | string, item_id: string | number) => {
        const removeItem = (items: StrategicPlanItem[], itemId: number | string): boolean => {
            for (let i = 0; i < items.length; i++) {
                if (items[i].id === itemId) {
                    items.splice(i, 1);
                    return true;
                }
                if (items[i].children && removeItem(items[i].children, itemId)) {
                    return true;
                }
            }
            return false;
        };
        // toggle parent accordion if it has 1 child
        // get toggle button by id
        const button = document.getElementById(`toggle_${parent_id}`);
        // const button = toggleButtonRef.current[parent_id];
        if (button && button.querySelector("[data-testid=\"caretDown-icon\"]")) {
            if (renderableData.ruuid === parent_id && renderableData.items.length === 1) {
                button.click();
            } else {
                const parent = findItemById(parent_id as number, renderableData.items);
                if (parent?.children.length === 1) {
                    button.click();
                }
            }
        }

        setEditableData(currentEditableData => {
            const newEditableData = { ...currentEditableData };
            if (newEditableData[parent_id]) {
                removeItem(newEditableData[parent_id].children, item_id);
            } else if (newEditableData[item_id]) {
                delete newEditableData[item_id];
            } else {
                for (const key in newEditableData) {
                    if (newEditableData[key.toString()].children) {
                        removeItem(newEditableData[key.toString()].children, item_id);
                    }
                }
            }

            return newEditableData;
        });
    }, [editableData]);

    function recursiveDisplayOrder(items: StrategicPlanItem[], id: number, displayOrder: number) {
        if (items.length === 0) {
            return;
        }
        let found = false;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.id === id) {
                item.display_order = displayOrder;
                found = true;
                break;
            }
            if (item.children) {
                recursiveDisplayOrder(item.children, id, displayOrder);
            }
        }
        if (found) {
            items.sort((a, b) => a.display_order - b.display_order);
        }
    }


    const renderableData = useMemo(() => {
        function mergeObjects() {
            const newdata = JSON.parse(JSON.stringify(strategicPlan!)) as StrategicPlanParent;

            const addChildToParent = (items: StrategicPlanItem[], parentId: string | number, itemToAdd: StrategicPlanItem): boolean => {
                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (item.id === parentId) {
                        item.children = item.children || [];
                        item.children.push(itemToAdd);
                        return true;
                    } else if (item.children && addChildToParent(item.children, parentId, itemToAdd)) {
                        return true;
                    }
                }
                return false;
            };

            Object.keys(editableData).forEach(k => {
                const item = editableData[k];
                if (item.parent_id === newdata.ruuid) {
                    newdata.items.push(item);
                } else {
                    addChildToParent(newdata.items, item.parent_id, item);
                }
            });
            if (droppableData.length > 0) {
                for (let i = 0; i < droppableData.length; i++) {
                    const item = droppableData[i];
                    // find the item in the newdata, even recursively and change the display order
                    recursiveDisplayOrder(newdata.items, Number(item.id), item.display_order);
                }
            }
            return newdata;
        }
        return mergeObjects();

    }, [strategicPlan, editableData, droppableData]);

    const sendNewItem = async (strategicPlanId: number | string, parentId: number, displayOrder: number, name?: string): Promise<CreateItemResponse> => {
        const res = await api.createStrategicPlanItem(String(strategicPlanId), {
            name: name ?? "",
            status: "active",
            display_order: displayOrder,
            parent_id: parentId === strategicPlanId ? null : parentId || null,
        });
        const result: CreateItemResponse = { success: true, data: res.data };
        return result;
    };


    const createItemsRecursively = async (items: StrategicPlanItem[], parentId?: number, data?: CustomFormData): Promise<ApiResponse[]> => {
        const createdItems: ApiResponse[] = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (parentId !== undefined) {
                item.parent_id = parentId;
            }

            const createdItem = await sendNewItem(
                item.strategic_plan_ruuid,
                item.parent_id,
                item.display_order,
                data?.[`${item.id}#input`]);

            createdItems.push(createdItem.data);
            if (item.children && item.children.length > 0) {
                const createdChildren = await createItemsRecursively(item.children, createdItem.data.data.id, data);
                createdItems.push(...createdChildren);
            }
        }
        return createdItems;
    };

    const submitEditedItems = async (itemList, formData) => {

        await Promise.all(itemList.map(async (item) => {
            await api.updateStrategicPlanItem(item.strategic_plan_ruuid, item.id, { parent_id: item.parent_id || item.strategic_plan_ruuid, name: formData[`${item.id}#input`] });
        }));
    };

    const submitEditDisplayOrder = async (items: StrategicPlanItem[]) => {
        await Promise.all(
            items.map(
                async (item) => await api.updateStrategicPlanItem(item.strategic_plan_ruuid, item.id.toString(), { display_order: item.display_order })));
    };

    const submitEditedStrategicPlan = async (item: Partial<StrategicPlanParent>) => {
        await api.updateStrategicPlan(
            item.ruuid!, {
            start_date: getFormatedDate(datePeriod?.start),
            end_date: getFormatedDate(datePeriod?.end),
            name: formData?.[`strategic_plan_name#${item.ruuid}`] ?? "",
            status: "active"
        });
    };

    const submitEdit = async () => {
        try {
            setHasFormError(false);
            showProcessing(true);
            if (Object.keys(editableData).length > 0) await createItemsRecursively(Object.values(editableData).flat(), undefined, formData);
            if (strategicPlanEdit) await submitEditedStrategicPlan(strategicPlanEdit);
            if (Object.keys(changedItems).length > 0) await submitEditedItems(changedItems, formData);
            if (droppableData.length > 0) await submitEditDisplayOrder(droppableData);
            await loadStrategicPlans();
            setEditableData({});
            handleCloseOnEdit();
            showProcessing(false);
            showAlert && showAlert("The changes have been saved.");
            setTimeout(() => {
                showAlert && showAlert("");
            }, 2500);
        } catch (error) {
            showProcessing(false);
            if (error.response.data.result.code === 400 || error.response.data.result.code === 409) {
                setShowDuplicatedValue(true);
                setErrorMessage(error.response.data.result.message);
                return;
            }

            showAlert && showAlert("An error has occurred. Try again later.");
            setTimeout(() => {
                showAlert && showAlert("");
            }, 2500);
        }
    };
    const handleOnEditParent = () => {
        if (!disable) {
            setOnEdit();
            if (renderableData.items.length === 0) {
                addData(renderableData.ruuid, renderableData.ruuid);
            }
        }
    };

    useEffect(() => {
        if (
            !hasFormError
            && (formData
                && (Object.keys(editableData).length > 0 || Object.keys(changedItems).length > 0 || droppableData.length > 0)
                || strategicPlanEdit)
        ) {
            submitEdit();
        }
    }, [formData]);

    useEffect(() => {
        if (!onEdit) {
            setEditableData({});
            setDroppableData([]);
        }
    }, [onEdit]);


    const methods = useForm<PlanFormData>();

    const handleCurrentItem = useCallback((item: StrategicPlanItem) => {
        setHasFormError(false);
        if (item?.newItem) return;
        const itemExists = changedItems.some((currentItem: StrategicPlanItem) => currentItem.id === item.id) || editableData[item.id];
        if (!itemExists) {
            setChangedItems((currentItems) => [...currentItems, item]);
        }
    }, [changedItems]);

    const handleChangeStrategicPlan = useCallback((item: Partial<StrategicPlanParent>) => {
        setHasFormError(false);
        if (strategicPlanEdit) return;
        setStrategicPlanEdit(item);
    }, [strategicPlanEdit]);

    const getDroppableLevelStep = (droppableId: string) => {
        if (droppableId.includes("droppableLevel1")) {
            return 1000000000;
        } else if (droppableId.includes("droppableLevel2")) {
            return 1000000;
        } else if (droppableId.includes("droppableLevel3")) {
            return 1000;
        } else {
            return 1;
        }
    };

    const getDroppableLevel = (droppableId: string) => {
        if (droppableId.includes("droppableLevel1")) {
            return 0;
        } else if (droppableId.includes("droppableLevel2")) {
            return 1;
        } else if (droppableId.includes("droppableLevel3")) {
            return 2;
        } else {
            return 3;
        }
    };

    const getLevelStep = (level: number) => {
        switch (level) {
            case 0:
                return 1000000000;
            case 1:
                return 1000000;
            case 2:
                return 1000;
            case 3:
                return 1;
            default:
                return 1;
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const parent = findParentItemByChildId(parseInt(result.draggableId), renderableData.items);
        const items = parent ? parent.children : renderableData.items;
        const sourceItem = items[result.source.index];
        const destinationItem = items[result.destination.index];
        const step = getDroppableLevelStep(result.destination.droppableId);
        const itemLevel = getDroppableLevel(result.destination.droppableId);
        sourceItem.display_order = result.source.index < result.destination.index ? destinationItem.display_order + step : destinationItem.display_order;
        let initialOrder = sourceItem.display_order + step;

        const newDroppableData = [...droppableData];
        const changeRecursivelyDisplayOrder = (children: StrategicPlanItem[], parentDisplayOrder: number, level = 0) => {
            if (children.length === 0 || level > 3) {
                return;
            }

            const levelStep = getLevelStep(level);
            for (let i = 0; i < children.length; i++) {
                const child = children[i];
                child.display_order = parentDisplayOrder + (i + 1) * levelStep;
                const sourceIndex = newDroppableData.findIndex((item) => item.id === child.id);
                if (sourceIndex > -1) {
                    newDroppableData[sourceIndex] = child;
                } else {
                    newDroppableData.push(child);
                }
                changeRecursivelyDisplayOrder(child.children, child.display_order, level + 1);
            }
        };

        const sourceIndex = newDroppableData.findIndex((item) => item.id === sourceItem.id);
        if (sourceIndex > -1) {
            newDroppableData[sourceIndex] = sourceItem;
        } else {
            newDroppableData.push(sourceItem);
        }
        changeRecursivelyDisplayOrder(sourceItem.children, sourceItem.display_order, itemLevel+1);
        const startAt = result.source.index < result.destination.index ? result.destination.index + 1 : result.destination.index;
        for (let i = startAt; i < items.length; i++) {
            if (i !== result.source.index) {
                const newItem = items[i];
                newItem.display_order = initialOrder;
                const changedIndex = newDroppableData.findIndex((item) => item.id === newItem.id);
                if (changedIndex > -1) {
                    newDroppableData[changedIndex] = newItem;
                } else {
                    newDroppableData.push(newItem);
                }
                changeRecursivelyDisplayOrder(newItem.children, newItem.display_order, itemLevel+1);
                initialOrder += step;
            }
        }
        setOnEdit();
        setDndChanged(true);
        setDroppableData(newDroppableData);
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        // some basic styles to make the items look a bit nicer
        userSelect: "none",
        // padding: grid * 2,
        // margin: `0 0 ${grid}px 0`,

        // change background colour if dragging
        background: isDragging ? "lightgreen" : "grey",

        // styles we need to apply on draggables
        ...draggableStyle
    });

    const getListStyle = isDraggingOver => ({
        background: isDraggingOver ? "lightblue" : "lightgrey",
    });

    const editStrategicPlanItems = useCallback((isEdit: boolean, strategicPlan?: StrategicPlanParent) => {

        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppableLevel1" type="droppableLevel1">
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            style={getListStyle(snapshot.isDraggingOver)}
                        >
                            {renderableData.items.map((item, index) => (
                                <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={getItemStyle(
                                                snapshot.isDragging,
                                                provided.draggableProps.style
                                            )}
                                        >
                                            <Accordion.Collapse eventKey={renderableData.ruuid} key={item.id}>
                                                <Accordion
                                                    defaultActiveKey={item.children.some((e) => e.name === "") ? `${item.id}` : ""}
                                                >
                                                    <ItemContainer
                                                        isEdit={isEdit}
                                                        toggleButtonRef={toggleButtonRef}
                                                        dragHandleProps={provided.dragHandleProps}
                                                        required={true}
                                                        inputName={`${item?.id}#input`}
                                                        controlledInput
                                                        defaultValue={item.name}
                                                        item={item}
                                                        level={0}
                                                        onChange={() => handleCurrentItem(item)}
                                                        onOpenArchive={() => {
                                                            item.status === "archived" ?
                                                                setOpenModal("unArchiveItem") : setOpenModal("archiveItem"),
                                                                setCurrentItem(item), setCurrentPlan(renderableData as StrategicPlanParent);
                                                        }}
                                                        onOpenDelete={() => {
                                                            if (strategicPlan && editableData[strategicPlan.ruuid]?.some((e: StrategicPlanItem) => e.id === item.id) || item.name === "") {
                                                                strategicPlan && removeData(strategicPlan.ruuid, item.id);
                                                            } else if (canDelete(item)) {
                                                                setOpenModal("delete");
                                                            } else {
                                                                setOpenModal("nonDelete");
                                                            }
                                                            setCurrentItem(item);
                                                        }}
                                                        eventKey={`${item.id}`}
                                                        onAddItem={() => {
                                                            setOnEdit();
                                                            addData(renderableData.ruuid, renderableData.ruuid);
                                                        }}
                                                        onAddSubItem={() => {
                                                            setOnEdit();
                                                            addSubItem(item.id, renderableData.ruuid);
                                                        }}
                                                    />
                                                    <Droppable droppableId={`droppableLevel2_${item.id}`} type={`droppableLevel2${item.id}`}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                {...provided.droppableProps}
                                                                ref={provided.innerRef}
                                                                style={getListStyle(snapshot.isDraggingOver)}
                                                            >
                                                                {item?.children?.map((subItem, subIndex) => (
                                                                    <Draggable key={subItem.id} draggableId={subItem.id.toString()} index={subIndex}>
                                                                        {(provided, snapshot) => (
                                                                            <div
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                style={getItemStyle(
                                                                                    snapshot.isDragging,
                                                                                    provided.draggableProps.style
                                                                                )}
                                                                            >
                                                                                <Accordion.Collapse eventKey={`${item.id}`} key={subItem.id}>
                                                                                    <Accordion
                                                                                        defaultActiveKey={subItem.children.some((e) => e.name === "") ? `${subItem.id}` : ""}
                                                                                    >
                                                                                        <ItemContainer
                                                                                            isEdit={isEdit}
                                                                                            toggleButtonRef={toggleButtonRef}
                                                                                            dragHandleProps={provided.dragHandleProps}
                                                                                            required
                                                                                            inputName={`${subItem?.id}#input`}
                                                                                            controlledInput
                                                                                            defaultValue={subItem.name}
                                                                                            item={subItem}
                                                                                            level={1}
                                                                                            onChange={() => handleCurrentItem(subItem)}
                                                                                            onOpenArchive={() => {
                                                                                                subItem.status === "archived" ?
                                                                                                    setOpenModal("unArchiveItem") : setOpenModal("archiveItem"),
                                                                                                    setCurrentItem(subItem), setCurrentPlan(renderableData);
                                                                                            }}
                                                                                            onOpenDelete={() => {
                                                                                                if (subItem.name === "" || editableData[subItem.parent_id]?.some((e) => e.id === subItem.id)) {
                                                                                                    removeData(subItem.parent_id, subItem.id);
                                                                                                } else if (canDelete(subItem)) {
                                                                                                    setOpenModal("delete");
                                                                                                    setCurrentItem(subItem);
                                                                                                } else {
                                                                                                    setOpenModal("nonDelete");
                                                                                                    setCurrentItem(subItem);
                                                                                                }
                                                                                            }}
                                                                                            eventKey={`${subItem.id}`}
                                                                                            onAddItem={() => {
                                                                                                setOnEdit();
                                                                                                addSubItem(item.id, renderableData.ruuid);
                                                                                            }}
                                                                                            onAddSubItem={() => {
                                                                                                setOnEdit();
                                                                                                addSubItem(subItem.id, renderableData.ruuid);
                                                                                            }}
                                                                                        />
                                                                                        <Droppable droppableId={`droppableLevel3_${subItem.id}`} type={`droppableLevel3${subItem.id}`}>
                                                                                            {(provided, snapshot) => (
                                                                                                <div
                                                                                                    {...provided.droppableProps}
                                                                                                    ref={provided.innerRef}
                                                                                                    style={getListStyle(snapshot.isDraggingOver)}
                                                                                                >
                                                                                                    {subItem?.children?.map((thirdItem, thirdIndex) =>
                                                                                                        <Draggable key={thirdItem.id} draggableId={thirdItem.id.toString()} index={thirdIndex}>
                                                                                                            {(provided, snapshot) => (
                                                                                                                <div
                                                                                                                    ref={provided.innerRef}
                                                                                                                    {...provided.draggableProps}
                                                                                                                    style={getItemStyle(
                                                                                                                        snapshot.isDragging,
                                                                                                                        provided.draggableProps.style
                                                                                                                    )}
                                                                                                                >
                                                                                                                    <Accordion.Collapse eventKey={`${subItem.id}`} key={thirdItem.id} >
                                                                                                                        <Accordion defaultActiveKey={thirdItem.children.some((e) => e.name === "") ? `${thirdItem.id}` : ""}>
                                                                                                                            <ItemContainer
                                                                                                                                isEdit={isEdit}
                                                                                                                                toggleButtonRef={toggleButtonRef}
                                                                                                                                dragHandleProps={provided.dragHandleProps}
                                                                                                                                required
                                                                                                                                inputName={`${thirdItem?.id}#input`}
                                                                                                                                controlledInput
                                                                                                                                defaultValue={thirdItem.name}
                                                                                                                                item={thirdItem}
                                                                                                                                level={2}
                                                                                                                                onChange={() => handleCurrentItem(thirdItem)}
                                                                                                                                onOpenArchive={() => {
                                                                                                                                    thirdItem.status === "archived" ?
                                                                                                                                        setOpenModal("unArchiveItem") : setOpenModal("archiveItem"),
                                                                                                                                        setCurrentItem(thirdItem), setCurrentPlan(renderableData);
                                                                                                                                }}
                                                                                                                                onOpenDelete={() => {
                                                                                                                                    if (thirdItem.name === "" || thirdItem.newItem ) {
                                                                                                                                        removeData(thirdItem.parent_id, thirdItem.id);
                                                                                                                                    } else if (canDelete(thirdItem)) {
                                                                                                                                        setCurrentItem(thirdItem);
                                                                                                                                        setOpenModal("delete");
                                                                                                                                    } else {
                                                                                                                                        setOpenModal("nonDelete");
                                                                                                                                        setCurrentItem(thirdItem);
                                                                                                                                    }
                                                                                                                                }}
                                                                                                                                eventKey={`${thirdItem.id}`}
                                                                                                                                onAddItem={() => {
                                                                                                                                    setOnEdit();
                                                                                                                                    addSubItem(subItem.id, renderableData.ruuid);
                                                                                                                                }}
                                                                                                                                onAddSubItem={() => {
                                                                                                                                    setOnEdit();
                                                                                                                                    addSubItem(thirdItem.id, renderableData.ruuid);
                                                                                                                                }}
                                                                                                                            />
                                                                                                                            <Droppable droppableId={`droppableLevel4_${thirdItem.id}`} type={`droppableLevel4${thirdItem.id}`}>
                                                                                                                                {(provided, snapshot) => (
                                                                                                                                    <div
                                                                                                                                        {...provided.droppableProps}
                                                                                                                                        ref={provided.innerRef}
                                                                                                                                        style={getListStyle(snapshot.isDraggingOver)}
                                                                                                                                    >
                                                                                                                                        {thirdItem?.children?.map((fourthItem, fourthIndex) =>
                                                                                                                                            <Draggable key={fourthItem.id} draggableId={fourthItem.id.toString()} index={fourthIndex}>
                                                                                                                                                {(provided, snapshot) => (
                                                                                                                                                    <div
                                                                                                                                                        ref={provided.innerRef}
                                                                                                                                                        {...provided.draggableProps}
                                                                                                                                                        style={getItemStyle(
                                                                                                                                                            snapshot.isDragging,
                                                                                                                                                            provided.draggableProps.style
                                                                                                                                                        )}
                                                                                                                                                    >
                                                                                                                                                        <Accordion.Collapse eventKey={`${thirdItem.id}`} key={fourthItem.id}>
                                                                                                                                                            <ItemContainer
                                                                                                                                                                isEdit={isEdit}
                                                                                                                                                                toggleButtonRef={toggleButtonRef}
                                                                                                                                                                dragHandleProps={provided.dragHandleProps}
                                                                                                                                                                controlledInput
                                                                                                                                                                required
                                                                                                                                                                inputName={`${fourthItem?.id}#input`}
                                                                                                                                                                defaultValue={fourthItem.name}
                                                                                                                                                                item={fourthItem}
                                                                                                                                                                level={3}
                                                                                                                                                                onChange={() => handleCurrentItem(fourthItem)}
                                                                                                                                                                onOpenArchive={() => {
                                                                                                                                                                    fourthItem.status === "archived" ?
                                                                                                                                                                        setOpenModal("unArchiveItem") : setOpenModal("archiveItem"),
                                                                                                                                                                        setCurrentItem(fourthItem);
                                                                                                                                                                }}
                                                                                                                                                                onOpenDelete={() => {
                                                                                                                                                                    if (fourthItem.name === "" || fourthItem.newItem ) {
                                                                                                                                                                        removeData(fourthItem.parent_id, fourthItem.id);
                                                                                                                                                                    } else if (canDelete(fourthItem)) {
                                                                                                                                                                        setCurrentItem(fourthItem);
                                                                                                                                                                        setOpenModal("delete");
                                                                                                                                                                    } else {
                                                                                                                                                                        setOpenModal("nonDelete");
                                                                                                                                                                        setCurrentItem(fourthItem);
                                                                                                                                                                    }
                                                                                                                                                                }}
                                                                                                                                                                eventKey={`${fourthItem.id}`}
                                                                                                                                                                onAddItem={() => {
                                                                                                                                                                    setOnEdit();
                                                                                                                                                                    addSubItem(thirdItem.id, renderableData.ruuid);
                                                                                                                                                                    toggleButtonRef.current[thirdItem.id]?.click();
                                                                                                                                                                }}
                                                                                                                                                            />
                                                                                                                                                        </Accordion.Collapse>
                                                                                                                                                    </div>
                                                                                                                                                )}
                                                                                                                                            </Draggable>
                                                                                                                                        )}
                                                                                                                                        {provided.placeholder}
                                                                                                                                    </div>
                                                                                                                                )}
                                                                                                                            </Droppable>
                                                                                                                        </Accordion>
                                                                                                                    </Accordion.Collapse>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </Draggable>
                                                                                                    )}
                                                                                                    {provided.placeholder}
                                                                                                </div>
                                                                                            )}
                                                                                        </Droppable>
                                                                                    </Accordion>
                                                                                </Accordion.Collapse>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {provided.placeholder}
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                </Accordion>
                                            </Accordion.Collapse>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }, [renderableData, changedItems]);


    return (
        <EditStrategicPlanFormContext.Provider value={{ strategicPlan: renderableData, setOnEdit, onEdit }}>
            <Accordion defaultActiveKey={""} className="strategic-plan-dropdown-container">
                <div className="general-data-container">
                <div className="not-status">
                    <div className="title-icons">
                        <div className="strategic-plan-name" >
                            <CustomToggle
                                eventKey={renderableData?.ruuid ?? "0"}
                                disabled={!renderableData?.items.length}
                                toggleButtonRef={toggleButtonRef} />
                            {(onEdit) && (
                                <ControlledInput
                                    className={"input-strategic-plans"}
                                    name={`strategic_plan_name#${renderableData.ruuid}`}
                                    placeholder={"Plan - Example"}
                                    required
                                    onInputChange={() => handleChangeStrategicPlan({ ruuid: renderableData.ruuid })}
                                    defaultValue={renderableData?.name}
                                />)}
                            {(!onEdit) && (<div className={`${renderableData?.status === "archived" ? "archived-item" : ""} text-click`}>
                                {renderableData?.name ? renderableData.name : "Plan - Example"}
                            </div>)}
                        </div>
                        <div className="options-icons">
                            {/* <DownloadIcon className="drop-icon"/> too add later*/}
                            <OverlayTrigger placement="top" overlay={<Tooltip className="tooltip-st-status">Make a copy of the plan</Tooltip>}>
                                <CopyIcon className={disable ? "drop-icon-disabled" : "drop-icon"}
                                    onClick={() => {
                                        if (!disable) {
                                            setOpenModal("copyPlan"),
                                                setCurrentPlan(renderableData!),
                                                setInputValue(`Copy of ${renderableData?.name}`);
                                        }
                                    }} />
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip className="tooltip-st-status">Enable plan edit mode </Tooltip>}>
                                <EditIcon className={disable ? "drop-icon-disabled" : "drop-icon"} onClick={handleOnEditParent} />
                            </OverlayTrigger>
                                {renderableData?.status === "archived" ?
                                <div
                                    className={`${disable ? "archived-item" : ""} unarchive-action`}
                                    onClick={() => {
                                        if (!disable) {
                                            setOpenModal("unarchive"),
                                                setCurrentPlan(renderableData!);
                                        }
                                    }}
                                >
                                    Unarchive
                                </div> :
                                    <OverlayTrigger overlay={<Tooltip className="tooltip-st-status">Archive the Plan</Tooltip>}>
                                        <ArchiveIcon className={disable ? "drop-icon-disabled" : "drop-icon"}
                                            onClick={() => {
                                                if (!disable) {
                                                    setOpenModal("archive"),
                                                        setCurrentPlan(renderableData!);
                                                }
                                            }}
                                        />
                                    </OverlayTrigger>
                            }

                            </div>
                        </div>
                        <div className="vertical-divider" />
                        <div className="strategic-plan-date">
                            <CalendarIcon className="drop-icon me-2" />
                            {onEdit ?
                                <>
                                    <ReactDatePicker
                                        placeholderText="Start Date"
                                        className="form-control me-2"
                                        maxDate={datePeriod.end}
                                        onChange={(start) => {
                                            handleChangeStrategicPlan({ ruuid: renderableData.ruuid, });
                                            start && (methods.setValue("start_date", start), setDatePeriod({ start: start, end: datePeriod.end }));
                                            setDatesChanged(true);
                                        }}
                                        selected={datePeriod.start}
                                    />
                                    <CalendarIcon className="drop-icon me-2" />
                                    <ReactDatePicker
                                        placeholderText="End Date"
                                        className="form-control me-2"
                                        minDate={datePeriod.start}
                                        onChange={(end) => {
                                            handleChangeStrategicPlan({ ruuid: renderableData.ruuid, });
                                            end && (methods.setValue("end_date", end), setDatePeriod({ start: datePeriod.start, end: end }));
                                            setDatesChanged(true);
                                        }}
                                        selected={datePeriod.end}
                                    />
                                </>
                                :
                                <div onClick={disable ? () => { } : setOnEdit} className="text-click">
                                    {renderableData?.start_date} - {renderableData?.end_date}
                                </div>
                            }
                        </div>
                        <div className="vertical-divider" />
                    </div>
                    <StatusCircle type={"open"} value={renderableData?.action_plan_statuses?.open_time ?? 0} />
                    <StatusCircle type={"achieved"} value={renderableData?.action_plan_statuses?.closed_goal_met ?? 0} />
                    <StatusCircle type={"unmet"} value={renderableData?.action_plan_statuses?.closed_goal_not_met ?? 0} />
                    <StatusCircle type={"no_date_assigned"} value={renderableData?.action_plan_statuses?.no_date_assigned ?? 0} />
                    <StatusCircle type={"overdue"} value={renderableData?.action_plan_statuses?.open_late ?? 0} />
                </div>
                {editStrategicPlanItems(onEdit, strategicPlan)}
            </Accordion>
            <StyledModal
                isOpen={openModal !== null}
                confirmButtonLabel={getConfirmLabel(openModal)}
                cancelButtonLabel={openModal !== "nonDelete" ? "Cancel" : undefined}
                onClose={() => setOpenModal(null)}
                title={getModalTitle(openModal)}
                subtitle={getSubtitleModal(openModal)}
                onSubmit={(e) => { onSubmit(e); }}
            />
            <StyledModal
                isOpen={openModal === "copyPlan"}
                confirmButtonLabel={getConfirmLabel(openModal)}
                cancelButtonLabel={openModal !== "nonDelete" ? "Cancel" : undefined}
                onClose={() => setOpenModal(null)}
                title={getModalTitle(openModal)}
                subtitle={getSubtitleModal(openModal)}
                icon={<CopyIcon className="copy-icon" />}
                onSubmit={(e) => {
                    e.preventDefault();
                    onCopyPlan({ name: inputValue, ruuid: currentPlan?.ruuid });
                    setOpenModal(null);
                }}
            >
                <input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className={"input-strategic-plans"}
                />
            </StyledModal>
            <StyledModal
                isOpen={showDuplicatedValue}
                confirmButtonLabel={"Got it"}
                onClose={() => setShowDuplicatedValue(false)}
                title={"Duplicated name"}
                subtitle={errorMessage}
                onSubmit={() => {
                    setShowDuplicatedValue(false);
                    setHasFormError(true);
                }}
            />
        </EditStrategicPlanFormContext.Provider>
    );
};